
import { Component, Vue, Watch, Prop, Inject, Ref } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import vSelect from "vue-select"
import FormGroup from "@/components/Fields/FormGroup.vue"
import UserImage from "@/components/UserImage.vue"
import UserSearch from "./UserSearch.vue"
import UserRoleDetails from "./UserRoleDetails.vue"

import { CaseDetails, User, CaseUser, CaseField } from "@/types"
import { UserRole as UserRoleType, UserRoleDefinition } from "@/types/caseUser"

import api from "@/services/api"

import { UserIdWithStatusAndFields, CurrentUser } from "@/types/user"
import UserHeaderFields from "./UserHeaderFields.vue"
import TimeDurationPicker from "./TimeDurationPicker.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { LocationLabels } from "@/types/location"
import { SmeLabel, smeLabels } from "@/types/sme"
import TimeAssignment from "./TimeAssignment.vue"
import UserAssignments from "./UserAssignments.vue"
import { TimeManagementSettings } from "@/types/timeManagement"
import apiDefaultCache from "@/services/apiDefaultCache"
import RecordAssignmentService from "@/services/recordAssignmentService"
import { TimeEntryInputFormat } from "@/types/timeEntry"

const caseStore = namespace("cases")

@Component({
	components: {
		"form-group": FormGroup,
		"v-select": vSelect,
		"user-image": UserImage,
		"user-search": UserSearch,
		"user-role": () => import("./UserRole.vue"),
		"user-header-fields": UserHeaderFields,
		"time-picker": TimeDurationPicker,
		"time-assignment": TimeAssignment,
		"user-assignments": UserAssignments,
		"user-role-details": UserRoleDetails
	}
})
export default class UserRole extends Vue {
	@Inject({ default: () => () => false }) featureEnabled!: (feature: TenantFeatures) => boolean
	@Inject({ from: "recordAssignmentService", default: null }) recordAssignmentService!: RecordAssignmentService

	@Ref() currentUserEntry!: HTMLElement[]

	@Prop()
	title!: string

	@Prop()
	userRole!: UserRoleDefinition

	@Prop()
	value!: UserRoleType | null

	@Prop()
	extensions: any

	// Note: check isReadonly instead of checking this directly.
	@Prop({ type: Boolean, default: false })
	readonly!: boolean

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public currentField!: CaseField

	@Prop({ type: String, default: null })
	templateId!: string | null

	@Prop({ default: () => [] })
	allSubRoleUsers!: CaseUser[]

	@Prop({ type: Boolean, default: true })
	displayUserAssignments!: boolean

	@Prop({ type: Boolean, default: false })
	excludeInactiveUsers!: boolean

	@Prop({ type: Boolean, default: false })
	hideTimeSpentControls!: boolean

	@State timeManagementSettings!: TimeManagementSettings | null
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getMe") me: CurrentUser | undefined
	@Getter("getIsUserInitComplete") isUserInitComplete!: boolean;

	LocationLabels = LocationLabels
	TimeEntryInputFormat = TimeEntryInputFormat

	newUsers: CaseUser[] = [];
	addingUser: boolean = false;
	addingExternalUser: boolean = false;
	addingToRole: any = null;

	confirmRemoveUserId: string | null = null
	userDataByUserId: { [userId:string] : UserIdWithStatusAndFields } = {}

	showTimeAsHoursMinutes: boolean = true
	locationFeatureEnabled: boolean = false
	reviewerAutomationFeatureEnabled: boolean = false
	isMounted = false

	smeType: SmeLabel | null = null
	smeUsers: User[] | null = null
	addingSme: boolean = false
	addingTimeAssignment: boolean = false
	hideAddSelfForTimeManagement: boolean = false

	public get users (): any[] {
		const users = this.userValues.map(user => {
			let userData = this.userDataByUserId![user.id]
			let headerValues = userData && userData.userHeaderFieldValues ? userData.userHeaderFieldValues : {}

			if (this.currentCase && this.currentCase.users) {
				let caseUser = this.currentCase.users.find(u => u.id === user.id)
				if (caseUser) {
					return {
						id: caseUser.id,
						name: caseUser.name,
						teams: caseUser.teams,
						email: caseUser.email,
						isExternal: caseUser.isExternal,
						timeSpent: user.timeSpent,
						timeEntries: user.timeEntries,
						userHeaderFieldValues: headerValues,
						location: user.location
					}
				}
			}

			if (this.newUsers) {
				const newUser = this.newUsers.find(u => u.id === user.id)
				if (newUser) {
					return {
						id: newUser.id,
						name: newUser.name,
						teams: newUser.teams,
						email: newUser.email,
						isExternal: newUser.isExternal,
						timeSpent: user.timeSpent,
						timeEntries: user.timeEntries,
						userHeaderFieldValues: headerValues,
						location: user.location
					}
				}
			}

			if (user.isExternal) {
				return {
					id: user.id,
					name: user.name ?? "",
					teams: user.teams ?? [],
					email: user.email,
					isExternal: user.isExternal,
					timeSpent: user.timeSpent,
					timeEntries: user.timeEntries,
					userHeaderFieldValues: headerValues,
					location: user.location
				}
			}

			return {
				id: user.id,
				name: "",
				teams: [],
				timeSpent: user.timeSpent,
				timeEntries: user.timeEntries,
				userHeaderFieldValues: headerValues,
				location: user.location
			}
		})

		const currentUserEntry = users.find(user => user.id === this.me?.id)
		if (currentUserEntry) {
			// Ensure the current user is always displayed first.
			users.splice(users.indexOf(currentUserEntry), 1)
			users.unshift(currentUserEntry)
		}

		return users
	}

	get showLocation (): boolean {
		return this.locationFeatureEnabled && !!this.userRole && !!this.userRole.allowLocationTracking
	}

	get userValues (): any[] {
		return this.value && this.value.users ? this.value.users : []
	}

	get userIds (): string[] {
		return this.userValues.map(user => user.id)
	}

	get subroles (): UserRoleType[] {
		if (!this.userRole || !this.userRole.subroles) return []

		return this.userRole.subroles.map(subrole => {
			const subroleValues = this.value && this.value.subRoles
				? this.value.subRoles.find(r => r.title === subrole.title)
				: null
			return {
				...subrole,
				users: subroleValues ? subroleValues.users || [] : [],
				smeType: this.smeType?.smeType ?? null
			}
		})
	}

	get canAddCurrentUser (): boolean {
		return !this.currentUserIsOnUserRole && (
			!this.extensions.teams || this.extensions.teams.length === 0 ||
			this.extensions.teams.some((teamId: string) => this.me?.teams.some(team => team.id === teamId))
		)
	}

	get hasHoursFieldOnCase (): boolean {
		const caseFields = this.currentCase?.fields
		if (!caseFields) return false

		let timeManagementFieldIds = this.extensions?.timeManagementHours
		if (!timeManagementFieldIds) return false

		timeManagementFieldIds = Array.isArray(timeManagementFieldIds) ? timeManagementFieldIds : [timeManagementFieldIds]
		if (timeManagementFieldIds.length === 0) return false

		return timeManagementFieldIds.some((id: string) => id in caseFields)
	}

	get useTimeManagement (): boolean {
		return !!this.currentCase && !!this.extensions && !!this.extensions.timeManagementHours &&
			!!this.timeManagementSettings?.enabled && !!this.assignmentDateField && this.hasHoursFieldOnCase
	}

	get overflowEnabled (): boolean {
		return !!this.timeManagementSettings && this.timeManagementSettings.overflowHours
	}

	get overflowFeatureEnabled (): boolean {
		return this.featureEnabled(TenantFeatures.TimeManagementOverflow)
	}

	/**
	 * Returns whether the component should be in a readonly state.
	 */
	get isReadonly (): boolean {
		return this.readonly || !this.isUserInitComplete
	}

	public async mounted () {
		this.showTimeAsHoursMinutes = this.featureEnabled(TenantFeatures.UserRoleTimeAsHoursMinutes)
		this.locationFeatureEnabled = this.featureEnabled(TenantFeatures.UserRoleLocation)
		this.reviewerAutomationFeatureEnabled = this.featureEnabled(TenantFeatures.ReviewerAutomation)

		this.smeType = (this.extensions && this.extensions.smeType)
			? smeLabels.firstOrDefault(smeLabel => smeLabel.smeType === this.extensions.smeType)
			: null

		if (this.value && this.value.users) {
			const usersNotOnCase = this.currentCase && this.currentCase.users
				? this.userValues.filter(user => !this.currentCase.users.find(u => u.id === user.id))
				: this.userValues

			const caseId = this.currentCase ? (this.currentCase.id ? this.currentCase.id : "") : ""
			const userPromises = usersNotOnCase.map(user =>
				this.getUserDetails(user, caseId)
			)
			await Promise.all(userPromises)
		}
		await Promise.all([this.bulkLoadUserData(), this.fetchSmeUsers()])
		this.isMounted = true
	}

	async getUserDetails (user: any, caseId: string) {
		if (user.isExternal) {
			return user
		} else if (user.external) {
			// handle differing property name in backend
			// safer fix than changing BSON property name
			user.isExternal = user.external
			return user
		}

		return api.userDetails(user.id, caseId).then(userDetails => {
			if (!this.newUsers.some(u => u.id === userDetails.id)) {
				this.newUsers.push({
					id: userDetails.id,
					name: userDetails.name,
					teams: userDetails.teams,
					email: userDetails.email,
					isExternal: userDetails.isExternal,
					location: userDetails.location
				})
			}
		})
	}

	public async addSelf () {
		if (!this.me || !this.canAddCurrentUser) {
			return
		}
		await this.addUser({
			id: this.me.id,
			name: this.me.name,
			email: this.me.email,
			isExternal: false,
			teams: this.me.teams
		})

		// Scroll to the current user entry into view.
		this.$nextTick().then(() => {
			if (this.currentUserEntry && this.currentUserEntry.length > 0) {
				try {
					this.currentUserEntry[0].scrollIntoView({ behavior: "smooth", block: "center" })
				} catch {
				}
			}
		})
	}

	public async addUser (user: any) {
		this.addingUser = false
		this.addingSme = false

		// don't add if already on another subrole
		if (this.allUserRoleUsersList.some(u => u.id === user.id)) {
			return
		}

		let userHeaderFieldValues: UserIdWithStatusAndFields | null = this.userDataByUserId[user.id] ?? null

		if (!userHeaderFieldValues) {
			userHeaderFieldValues = (await apiDefaultCache.userStatusListWithFieldsGet([user.id])).findAndGet(i => i.userId === user.id, i => i, null)
			if (userHeaderFieldValues) {
				this.userDataByUserId[user.id] = userHeaderFieldValues
			}
		}

		if (!this.newUsers.some(u => u.id === user.id)) {
			this.newUsers.push({
				id: user.id,
				name: user.name === undefined ? user.title : user.name,
				email: user.email,
				isExternal: user.isExternal,
				teams: user.teams,
				timeSpent: 0,
				location: null
			})
		}

		const userList = this.userValues
		userList.push({
			id: user.id,
			timeSpent: user.timeSpent || 0,
			name: user.name,
			email: user.email,
			isExternal: user.isExternal,
			location: null,
			userHeaderFieldValues
		})

		this.$emit("input", {
			...this.value,
			users: userList
		})
	}

	public removeUser (user: User) {
		this.confirmRemoveUserId = null
		this.$emit("input", {
			...this.value,
			users: this.userValues.filter(u => u.id !== user.id)
		})
	}

	public updateRole (role: any, value: any) {
		Vue.set(role, "users", value.users)

		const newValue = {
			...this.value,
			subRoles: this.userRole.subroles
				? this.userRole.subroles.map(subrole => {
					let subroleValues = this.value && this.value.subRoles
						? this.value.subRoles.find((r: any) => r.title === subrole.title)
						: null

					if (!subroleValues) {
						subroleValues = {
							title: subrole.title,
							users: [],
							subRoles: null // Sub-sub roles are not supported.
						}
					}

					return {
						title: subrole.title,
						users: subrole.title === role.title
							? value.users
							: subroleValues.users
					}
				})
				: null
		}
		this.$emit("input", newValue)
	}

	public updateUser (user: CaseUser) {
		if (!this.value || !this.value.users) return

		const updatedUsers = [...this.value.users]
		const userIdx = updatedUsers.findIndex(u => u.id === user.id)
		if (userIdx === -1) return

		updatedUsers.splice(userIdx, 1, user)
		this.$emit("input", {
			...this.value,
			users: updatedUsers
		})
	}

	public get assignedTemplateId (): string | null {
		return this.templateId ?? this.currentCase?.templateId ?? null
	}

	@Watch("userIds")
	private async bulkLoadUserData () : Promise<void> {
		if (!this.userIds || this.userIds.length === 0) {
			return
		}

		// Only fetch data we need to
		const outstandingUsers = this.userIds.filter(id => !this.userDataByUserId![id])

		if (outstandingUsers.length === 0) return

		const userIdsWithData = await apiDefaultCache.userStatusListWithFieldsGet(outstandingUsers)
		userIdsWithData.forEach(userIdWithData => {
			Vue.set(this.userDataByUserId!, userIdWithData.userId, userIdWithData)
		})
	}

	public async fetchSmeUsers () {
		if (!this.assignedTemplateId || !this.smeType) {
			return
		}
		this.smeUsers = await api.smeUsersForTemplate(this.smeType!.smeType, this.assignedTemplateId)
	}

	public get smeTypeDisplay (): string {
		return this.smeType?.label ?? ""
	}

	public get smeUserOptions (): { label: string, user: User }[] {
		if (!this.smeUsers) {
			return []
		}
		let options: { label: string, user: User }[] = []
		this.smeUsers.forEach(user => options.push({ label: user.name, user: user }))
		options.removeWhere(option => this.userIsOnUserRole(option.user))
		options.sort((a, b) => a.user.name.localeCompare(b.user.name))
		return options
	}

	public userPicture (userDisplayObject: any): string {
		if (!userDisplayObject || !userDisplayObject.user) {
			return ""
		}

		let searchItem = userDisplayObject.user
		if (searchItem.imageUrl) {
			return searchItem.imageUrl!
		} else if (searchItem.pictureUrl) {
			return searchItem.pictureUrl!
		} else {
			return api.pictureUrl(searchItem.id)
		}
	}

	public loadedImage (_result: any) {
		// do nothing here - this is just a handler to tell the browser not to show it until it's loaded
	}

	public userIsOnUserRole (user: User): boolean {
		return !!user && this.allUserRoleUsersList.some(item => item.id === user.id)
	}

	public get currentUserIsOnUserRole (): boolean {
		return !!this.me && this.userIsOnUserRole(this.me)
	}

	public get currentUserIsSme (): boolean {
		return !!this.me && !!this.smeUsers?.some(user => user.id === this.me!.id)
	}

	get allSubRoleUsersList (): CaseUser[] {
		if (!this.value?.subRoles || this.value.subRoles.length === 0) {
			return []
		}

		return this.value.subRoles!.flatMap(subrole => subrole.users ?? [])
	}

	get allUserRoleUsersList (): CaseUser[] {
		return [...this.users, ...this.allSubRoleUsers]
	}

	addUserStart () {
		this.addingUser = true
		this.addingExternalUser = false
	}

	get appendDropdownToBody () {
		return this.currentCase === undefined || this.currentCase === null // Custom styling for when the parent component is not a case to fix dropdown list visibility issue
	}

	/**
	 * Gets the assignment date in ISO format, using assignment date field from time management settings.
	 * @returns {string | null} - The assignment date in ISO format, or null if not available.
	 */
	get assignmentDate (): string | null {
		const assignmentDateField = this.assignmentDateField
		if (!assignmentDateField) return null // The field does not exist on the record

		if (assignmentDateField.value instanceof Date) {
			return assignmentDateField.value.toISOString()
		} else {
			return assignmentDateField.value
		}
	}

	get assignmentDateField (): CaseField | null {
		if (!this.recordAssignmentService || !this.currentField || !this.timeManagementSettings) return null
		return this.recordAssignmentService.getAssignmentDate(this.currentField.field, this.timeManagementSettings!)
	}

	private handleFieldAwaitingUpdate (fieldId: string | undefined) {
		this.$emit("field-awaiting-update", fieldId)
	}

	get showAddYourselfButtonOnTimeManagementUserRolesFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ShowAddYourselfButtonOnTimeManagementEnabledUserRoles)
	}

	private setUserTimeSpent (userId: string, timeSpent: number) {
		const userIndex = this.userValues.findIndex(user => user.id === userId)
		if (userIndex !== -1) {
			this.userValues[userIndex].timeSpent = timeSpent
		}
	}
}

